/**
 * @file
 * Contact form (webform "feedback") client-side validation.
 *
 * - Required fields cannot be empty ("Tu nombre", "Tu e-mail", "Mensaje", "Tema de contacto").
 * - Email format must be valid ("El formato del correo es incorrecto").
 * - The data processing (GDPR) checkbox must be checked.
 * - "Perfil profesional" is optional and can be left empty.
 * - Error messages appear directly beneath the corresponding field and are
 *   linked to it (aria-describedby / aria-invalid) so assistive technology
 *   reads them when the field is focused.
 * - A field is only validated once the user has interacted with it (blur or
 *   change), never on the way in, so tabbing through the form does not raise
 *   errors prematurely.
 * - The submit button keeps its disabled look until every required field is
 *   valid (design decision). It is marked with aria-disabled instead of the
 *   disabled attribute so it stays focusable and announced; activating it
 *   while the form is invalid shows every message, announces a summary and
 *   moves focus to it instead of silently doing nothing (WCAG 3.3.1 / 4.1.3).
 */

(function (Drupal, drupalSettings, once) {
  'use strict';

  /**
   * Detects current language from drupalSettings or URL prefix.
   */
  function getCurrentLang() {
    if (drupalSettings && drupalSettings.path && drupalSettings.path.currentLanguage) {
      return drupalSettings.path.currentLanguage;
    }
    // Fallback: check URL prefix (e.g. /en/contact)
    const match = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/);
    return match ? match[1] : 'es';
  }

  /**
   * Returns a translated string based on current language.
   * Supported languages: 'es' (default), 'en'.
   */
  function t(key) {
    const lang = getCurrentLang();
    const translations = {
      'required': {
        es: 'Este campo es obligatorio.',
        en: 'This field is required.'
      },
      'privacy': {
        es: 'Debes aceptar el tratamiento de tus datos para enviar el formulario.',
        en: 'You must accept the processing of your data to submit the form.'
      },
      'email_invalid': {
        es: 'El formato del correo es incorrecto',
        en: 'The email format is incorrect'
      },
      'global_error_title': {
        es: 'Alguno de los datos que nos has proporcionado es incorrecto.',
        en: 'Some of the data you have provided is incorrect.'
      },
      'global_error_body': {
        es: 'Por favor, revisa los campos del formulario e inténtalo de nuevo.',
        en: 'Please review the form fields and try again.'
      }
    };
    const entry = translations[key];
    if (!entry) return key;
    return entry[lang] || entry['es'];
  }

  /**
   * Validates email syntax using standard pattern.
   */
  function isValidEmail(email) {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email.trim());
  }

  Drupal.behaviors.contactFormValidation = {
    attach: function (context) {
      const forms = once(
        'contact-form-validation',
        'form.webform-submission-feedback-form, form.webform-submission-feedback-add-form, form[id^="webform-submission-feedback"]',
        context
      );

      forms.forEach(function (form) {
        const submitBtn = form.querySelector('input[type="submit"], button[type="submit"]');
        if (!submitBtn) {
          return;
        }

        // The behavior takes over the browser's native bubbles so every error
        // is shown at once, in the site's language and linked to its field.
        // Without JS the native constraint validation still applies.
        form.noValidate = true;

        // Fields the user has interacted with (blurred or changed).
        const touchedFields = new Set();

        /**
         * Gets all required controls in the contact form.
         */
        function getRequiredFields() {
          const controls = Array.from(
            form.querySelectorAll('input:not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="reset"]), select, textarea')
          );

          return controls.filter(function (control) {
            // Explicitly exclude optional field_professional_profile
            if (control.name && control.name.includes('field_professional_profile')) {
              return false;
            }
            if (control.hasAttribute('required') || control.required) {
              return true;
            }
            if (control.getAttribute('aria-required') === 'true') {
              return true;
            }
            if (control.closest('.form-required, .js-form-required, .form-item-required')) {
              return true;
            }
            if (control.id) {
              const label = form.querySelector('label[for="' + control.id + '"]');
              if (label && (label.classList.contains('form-required') || label.classList.contains('js-form-required') || label.querySelector('.form-required, .js-form-required'))) {
                return true;
              }
            }
            return false;
          });
        }

        /**
         * Evaluates validation error for a specific field.
         * Returns null if valid, or a translatable error string if invalid.
         */
        function getFieldError(field) {
          const type = field.type ? field.type.toLowerCase() : '';
          const name = field.name || '';

          if (type === 'checkbox') {
            if (!field.checked) {
              if (name.includes('privacy') || name.includes('politica_privacidad')) {
                return t('privacy');
              }
              return t('required');
            }
            return null;
          }

          if (type === 'radio') {
            const group = form.querySelectorAll('input[name="' + field.name + '"]');
            const checked = Array.from(group).some(function (r) { return r.checked; });
            return checked ? null : t('required');
          }

          const val = field.value ? field.value.trim() : '';

          if (val === '' || val === '_none') {
            return t('required');
          }

          if (type === 'email' || name === 'mail') {
            if (!isValidEmail(val)) {
              return t('email_invalid');
            }
          }

          return null;
        }

        /**
         * Returns the id used for a field's inline error message.
         */
        function getErrorId(field) {
          return (field.id || field.name) + '--error';
        }

        /**
         * Renders or removes the inline error message beneath the field element
         * and keeps the field's ARIA state in sync.
         */
        function updateFieldErrorUI(field, errorText, showErrorMessage) {
          const container = field.closest('.form-item, .form-check, .js-form-item') || field.parentNode;
          let errorElem = container.querySelector('.field-error-message');

          if (errorText && showErrorMessage) {
            field.classList.add('is-invalid');
            field.setAttribute('aria-invalid', 'true');

            if (!errorElem) {
              errorElem = document.createElement('div');
              errorElem.className = 'invalid-feedback field-error-message text-danger mt-1 form-item--error-message';
              errorElem.id = getErrorId(field);
              container.appendChild(errorElem);
            }
            errorElem.textContent = errorText;
            errorElem.style.display = 'block';
            field.setAttribute('aria-describedby', errorElem.id);
          } else {
            field.classList.remove('is-invalid');
            field.removeAttribute('aria-invalid');
            if (errorElem) {
              errorElem.style.display = 'none';
              if (field.getAttribute('aria-describedby') === errorElem.id) {
                field.removeAttribute('aria-describedby');
              }
            }
          }
        }

        /**
         * Shows or hides the error summary placed after the form title.
         */
        function updateGlobalError(show) {
          let globalErrorElem = form.querySelector('.global-contact-error');

          if (show) {
            if (!globalErrorElem) {
              globalErrorElem = document.createElement('div');
              globalErrorElem.className = 'global-contact-error text-danger mt-3 mb-4';
              globalErrorElem.setAttribute('role', 'alert');
              globalErrorElem.setAttribute('tabindex', '-1');
              globalErrorElem.innerHTML = '<p class="mb-3 fw-bold">' + t('global_error_title') + '</p><p class="mb-0">' + t('global_error_body') + '</p>';
              const titleElem = form.querySelector('.contact-form-title') || form.querySelector('.page-title');
              if (titleElem) {
                titleElem.parentNode.insertBefore(globalErrorElem, titleElem.nextSibling);
              } else {
                form.insertBefore(globalErrorElem, form.firstChild);
              }
            }
            globalErrorElem.style.display = 'block';
          } else if (globalErrorElem) {
            globalErrorElem.style.display = 'none';
          }

          return globalErrorElem;
        }

        /**
         * Reflects the form validity on the submit button.
         *
         * aria-disabled (not disabled) keeps the button in the tab order and
         * lets a click/Enter trigger the submit handler, which then reveals
         * the pending errors.
         */
        function updateSubmitState(isValid) {
          if (isValid) {
            submitBtn.removeAttribute('aria-disabled');
            submitBtn.classList.remove('disabled');
          } else {
            submitBtn.setAttribute('aria-disabled', 'true');
            submitBtn.classList.add('disabled');
          }
        }

        /**
         * Validates the required fields and updates the inline messages.
         *
         * @param {boolean} showAllErrors
         *   TRUE on a submit attempt: every error is shown regardless of
         *   whether the field has been touched.
         *
         * @return {HTMLElement[]}
         *   The fields that are currently invalid.
         */
        function validateForm(showAllErrors) {
          const invalidFields = [];

          getRequiredFields().forEach(function (field) {
            const error = getFieldError(field);
            const isTouched = showAllErrors || touchedFields.has(field);

            if (error) {
              invalidFields.push(field);
              updateFieldErrorUI(field, error, isTouched);
            } else {
              updateFieldErrorUI(field, null, false);
            }
          });

          updateSubmitState(invalidFields.length === 0);

          return invalidFields;
        }

        // Field-level validation once the user has interacted with the field.
        // "keyup" is deliberately not used: the keyup of the Tab key lands on
        // the newly focused field and would mark it invalid before typing.
        getRequiredFields().forEach(function (field) {
          const markTouched = function () {
            touchedFields.add(field);
            if (validateForm(false).length === 0) {
              updateGlobalError(false);
            }
          };

          field.addEventListener('blur', markTouched);
          field.addEventListener('change', markTouched);
          // Clear a visible error as soon as it is corrected.
          field.addEventListener('input', function () {
            if (touchedFields.has(field)) {
              validateForm(false);
            }
          });
        });

        // On submit (also when the button looks disabled): show every error,
        // announce the summary and move focus to it so keyboard and screen
        // reader users know why the form was not sent.
        form.addEventListener('submit', function (e) {
          const invalidFields = validateForm(true);
          if (invalidFields.length > 0) {
            e.preventDefault();
            e.stopPropagation();
            invalidFields.forEach(function (field) { touchedFields.add(field); });
            const summary = updateGlobalError(true);
            if (summary) {
              summary.focus();
            }
          } else {
            updateGlobalError(false);
          }
        });

        // Initial state: the button looks disabled until the form is valid.
        validateForm(false);
      });
    }
  };
})(Drupal, drupalSettings, once);
